import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminAccessEditorFields,
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminMemberAccessDetail,
  updateYowanAdminMemberAccess,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const AdminMemberAccessEditPage = () => {
  const content = yowanPageContent['/admin/access/member/:accessId/edit'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { accessId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        memberId: '',
        isActive: true,
      },
  });

  useEffect(() => {
    if (!content || !accessId) {
      return undefined;
    }

    let isMounted = true;

    const loadAccess = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const record = await fetchYowanAdminMemberAccessDetail(accessId);

        if (!isMounted) {
          return;
        }

        setCurrentRecord(record);
        reset(record);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setLoadError(content.form.loadErrorMessage);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAccess();

    return () => {
      isMounted = false;
    };
  }, [accessId, content, reset]);

  if (!content) {
    return null;
  }

  const memberOptions = currentRecord
    ? [
        {
          value: currentRecord.memberId,
          label: `${currentRecord.accountName || '氏名未設定'} / ${currentRecord.accountEmail || '-'}`,
        },
      ]
    : content.form.memberOptions;
  const pageContent = {
    ...content,
    form: {
      ...content.form,
      memberOptions,
    },
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminMemberAccess(accessId, values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(content.form.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/access/member" routeKey="/admin/access/member/:accessId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      {isLoading || loadError || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : loadError || content.form.loadErrorMessage}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          <AdminAccessEditorFields
            content={pageContent}
            register={register}
            errors={errors}
            memberLocked
            lockedMemberId={currentRecord.memberId}
          />

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
            </button>
          </div>

          {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminMemberAccessEditPage;
